import { useContext, useState } from "react";
import HeaderBar from "./components/headerBar";
import { enrollmentDataContext } from "./layout";
import {
  Award,
  Copy,
  Share2,
  ExternalLink,
  Lock,
  Zap,
  CheckCircle2,
  Trophy,
  ShieldCheck,
  Loader,
  AlertTriangle,
  X
} from "lucide-react";
import Button from "../../components/common/Button";
import { useToast } from "../../hooks/otherHooks/useToast";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";


const CERT_TEMPLATES = [
  "https://marketplace.canva.com/EAFIE46v97U/1/0/1600w/canva-blue-and-gold-elegant-certificate-of-appreciation-1v-8T4r-w8U.jpg",
  "https://marketplace.canva.com/EAFH9-E9S9I/1/0/1600w/canva-minimalist-certificate-of-participation-landscape-U_L0p_XnS_8.jpg"
];

const LOCKED_TEMPLATE = "https://marketplace.canva.com/EAFNl9H0Y9o/1/0/1600w/canva-clean-professional-certificate-of-completion-landscape-L7mR_6eF-Yw.jpg";

const CertificateCard = ({ title, date, id, image, isLocked, progress = 0, onCopy, ...props }: any) => (
  <div className={`cht-group cht-relative cht-bg-white cht-rounded-[32px] cht-overflow-hidden cht-shadow-[0_10px_40px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50 cht-transition-all cht-duration-500 ${!isLocked ? 'hover:cht-shadow-2xl hover:-cht-translate-y-2' : ''}`}>
    {/* Certificate Preview Container */}
    <div className={`cht-relative cht-aspect-[1.4/1] cht-overflow-hidden cht-bg-gray-50 ${isLocked ? 'cht-grayscale cht-opacity-60' : ''}`}>
      <img src={image} alt={title} className="cht-w-full cht-h-full cht-object-cover" />

      {/* Apple-style Holographic Shine Effect */}
      {!isLocked && (
        <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-tr cht-from-white/0 cht-via-white/40 cht-to-white/0 -cht-translate-x-full group-hover:cht-translate-x-full cht-transition-transform cht-duration-[1500ms] cht-pointer-events-none"></div>
      )}

      {/* Locked Overlay */}
      {isLocked && (
        <div className="cht-absolute cht-inset-0 cht-bg-black/45 cht-backdrop-blur-[2px] cht-flex cht-flex-col cht-items-center cht-justify-center cht-text-white cht-space-y-3">
          <div className="cht-w-12 cht-h-12 cht-bg-white/20 cht-backdrop-blur-md cht-rounded-full cht-flex cht-items-center cht-justify-center cht-border cht-border-white/20 shadow-xl">
            <Lock size={18} />
          </div>
          <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-[0.2em]">Locked Credential</span>
        </div>
      )}
    </div>

    {/* Content */}
    <div className="cht-p-8 cht-space-y-6">
      <div className="cht-space-y-2">
        <h3 className={`cht-text-lg cht-font-black cht-text-black cht-line-clamp-1 ${!isLocked ? 'group-hover:cht-text-[#3e57da]' : 'cht-text-gray-400'} cht-transition-colors`}>
          {title}
        </h3>
        <div className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest cht-flex cht-items-center cht-gap-2">
          {isLocked ? `In Progress: ${progress}%` : `Issued: ${date}`}
          {!isLocked && <span className="cht-text-green-500 cht-flex cht-items-center cht-gap-1"><CheckCircle2 size={10} /> Verified</span>}
        </div>
      </div>

      {isLocked ? (
        <div className="cht-space-y-3">
          <div className="cht-w-full cht-bg-gray-100 cht-h-1.5 cht-rounded-full cht-overflow-hidden">
            <div className="cht-bg-[#3e57da] cht-h-full cht-rounded-full transition-all duration-1000" style={{ width: `${progress}%` }}></div>
          </div>
          <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase">Complete remaining {100 - progress}% to unlock</p>
        </div>
      ) : (
        <div className="cht-grid cht-grid-cols-2 cht-gap-3">
          <Button
            variant="secondary"
            size="sm"
            className="cht-bg-[#0077b5] cht-border-none hover:cht-bg-[#006097] cht-text-white"
            leftIcon={<Award size={14} />}
            onClick={() => {
              window.open("https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME", "_blank");
            }}
          >
            Add to Profile
          </Button>
          <Button
            variant="tab"
            size="sm"
            leftIcon={<Copy size={14} />}
            onClick={() => onCopy(id)}
          >
            Copy ID
          </Button>
        </div>
      )}
    </div>
  </div>
);

const Certification = () => {
  const { addToast } = useToast();
  const { inProgressCourses, completedCourses, inProgressTracks, completedTracks } = useContext(enrollmentDataContext);

  const ongoing = [...(inProgressTracks || []), ...(inProgressCourses || [])];
  const completed = [...(completedTracks || []), ...(completedCourses || [])];

  const handleCopyId = (certId) => {
    navigator.clipboard.writeText(certId);
    addToast({
      message: "Credential ID copied to clipboard!",
      bg: "success"
    });
  };

  // Certificate Verification Lookup states
  const [searchId, setSearchId] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState(null);

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (!searchId.trim()) {
      addToast({
        message: "Please enter a Certificate ID to verify.",
        bg: "warning"
      });
      return;
    }

    setVerifying(true);
    setVerificationError(null);
    setVerificationResult(null);

    try {
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const response = await axios.get(`${baseUrl}/course/bk-verify-certificate/${searchId.trim()}/`);
      setVerificationResult(response.data);
      addToast({
        message: "Certificate validity successfully verified!",
        bg: "success"
      });
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.detail || "Certificate verification failed. Check the ID and try again.";
      setVerificationError(errMsg);
      addToast({
        message: "Verification failed. Invalid Certificate ID.",
        bg: "danger"
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleCloseVerification = () => {
    setVerificationResult(null);
    setVerificationError(null);
  };

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Professional Credentials" />

      <main className="cht-container cht-space-y-12 pb-12">

        {/* Wall of Fame Hero */}
        <div className="cht-bg-white/40 cht-backdrop-blur-xl cht-p-10 cht-rounded-[48px] cht-border cht-border-white/60 cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-gap-10">
          <div className="cht-space-y-6 cht-flex-1">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-12 cht-h-12 cht-bg-[#3e57da] cht-rounded-[18px] cht-flex cht-items-center cht-justify-center shadow-lg shadow-[#3e57da]/30">
                <Trophy size={24} className="cht-text-white" />
              </div>
              <h2 className="cht-text-2xl cht-font-black cht-text-black">Your Achievement Wall</h2>
            </div>
            <p className="cht-text-sm cht-text-gray-500 cht-font-medium leading-relaxed cht-max-w-md">
              Each certificate from Codehub Technologies is globally verified and recognized by our network of partner recruiters. Showcase your expertise to the world.
            </p>
            <div className="cht-flex cht-items-center cht-gap-6">
              <div className="cht-flex cht-flex-col">
                <span className="cht-text-2xl cht-font-black text-black">
                  {String(completed.length).padStart(2, "0")}
                </span>
                <span className="cht-text-[10px] cht-font-bold text-gray-400 uppercase tracking-widest">Earned</span>
              </div>
              <div className="cht-w-px cht-h-8 cht-bg-gray-200"></div>
              <div className="cht-flex cht-flex-col">
                <span className="cht-text-2xl cht-font-black text-black">
                  {String(ongoing.length).padStart(2, "0")}
                </span>
                <span className="cht-text-[10px] cht-font-bold text-gray-400 uppercase tracking-widest">Pending</span>
              </div>
            </div>
          </div>
          <div className="cht-w-full md:cht-w-fit">
            <Button
              variant="secondary"
              size="lg"
              className="cht-w-full"
              leftIcon={<Share2 size={18} />}
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/student/credentials/public`);
                addToast({
                  message: "Public profile link copied!",
                  bg: "success"
                });
              }}
            >
              Public Profile Link
            </Button>
          </div>
        </div>

        {/* Certificate Lookup Bar */}
        <div className="cht-bg-white/40 cht-backdrop-blur-xl cht-p-8 cht-rounded-[32px] cht-border cht-border-white/60 cht-shadow-[0_10px_40px_rgba(0,0,0,0.02)]">
          <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center cht-justify-between cht-gap-6">
            <div className="cht-space-y-1">
              <h3 className="cht-text-lg cht-font-black cht-text-black cht-flex cht-items-center cht-gap-2">
                <ShieldCheck size={20} className="cht-text-[#3e57da]" />
                Verify Certificate Authenticity
              </h3>
              <p className="cht-text-xs cht-text-gray-400 cht-font-medium">
                Recruiters and employers can check the cryptographic validity of any Codehub Technologies credential instantly.
              </p>
            </div>

            <form onSubmit={handleVerifySubmit} className="cht-flex-1 cht-max-w-xl cht-w-full cht-flex cht-gap-3">
              <div className="cht-relative cht-flex-1">
                <input
                  type="text"
                  placeholder="Enter Certificate ID (e.g. CH-1-CS-2026)"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  className="cht-w-full cht-bg-white/80 cht-border cht-border-gray-200 focus:cht-border-[#3e57da] cht-h-12 cht-px-4 cht-pl-11 cht-rounded-xl cht-text-sm cht-font-bold cht-placeholder-gray-400 focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
                />
                {/* <Search size={16} className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" /> */}
              </div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="cht-h-12 cht-px-6 cht-bg-[#3e57da] hover:cht-bg-[#2f42a9]"
                isLoading={verifying}
              >
                Verify
              </Button>
            </form>
          </div>
        </div>

        {/* Credential Grid */}
        <section className="cht-space-y-8">
          <div className="cht-flex cht-items-center cht-justify-between cht-px-2">
            <h3 className="cht-text-xl cht-font-black cht-text-black">Credential Gallery</h3>
            <span className="cht-text-xs cht-font-bold cht-text-[#3e57da] flex items-center gap-1">All Credentials <ExternalLink size={12} /></span>
          </div>

          {(completed.length > 0 || ongoing.length > 0) ? (
            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-8">
              {/* Unlocked / Completed Certificates */}
              {completed.map((item, idx) => {
                const title = item.course_detail?.title || item.track_detail?.title || "Professional Certificate";
                const dateStr = item.payment_date || item.date;
                const formattedDate = dateStr
                  ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : "May 30, 2026";
                const certId = `CH-${item.id}-${item.course_detail ? 'CS' : 'TR'}-2026`;
                const image = CERT_TEMPLATES[idx % CERT_TEMPLATES.length];

                return (
                  <CertificateCard
                    key={`comp-${idx}`}
                    title={title}
                    date={formattedDate}
                    id={certId}
                    image={image}
                    isLocked={false}
                    onCopy={handleCopyId}
                  />
                );
              })}

              {/* Locked / In Progress Certificates */}
              {ongoing.map((item, idx) => {
                const title = item.course_detail?.title || item.track_detail?.title || "In-Progress Course";
                const progress = item.progress_percent ?? item.progress ?? 0;

                return (
                  <CertificateCard
                    key={`prog-${idx}`}
                    title={title}
                    progress={progress}
                    image={LOCKED_TEMPLATE}
                    isLocked={true}
                    onCopy={handleCopyId}
                  />
                );
              })}
            </div>
          ) : (
            <div className="cht-p-20 cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-6 cht-bg-white cht-rounded-[32px] cht-border cht-border-gray-50">
              <div className="cht-w-24 cht-h-24 cht-bg-slate-50 cht-rounded-full cht-flex cht-items-center cht-justify-center">
                <Trophy className="cht-text-slate-300 cht-w-10 cht-h-10" />
              </div>
              <div className="cht-space-y-2">
                <h3 className="cht-text-xl cht-font-bold cht-text-black">No active credentials</h3>
                <p className="cht-text-sm cht-text-gray-400 cht-max-w-xs">
                  Your professional certificates will appear here once you enroll in your first training and start completing modules.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Motivation / Support */}
        <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-p-8 cht-bg-gray-50 cht-rounded-[32px] cht-gap-6">
          <div className="cht-flex cht-items-center cht-gap-4">
            <div className="cht-w-10 cht-h-10 cht-bg-[#3e57da]/10 cht-rounded-full cht-flex cht-items-center cht-justify-center">
              <Zap size={18} className="cht-text-[#3e57da]" />
            </div>
            <p className="cht-text-sm font-bold text-gray-500">Each certificate adds <span className="cht-text-black">15+ Skills</span> to your LinkedIn verification list.</p>
          </div>
          <Button variant="ghost" size="sm" className="cht-text-[#3e57da]">How verification works?</Button>
        </div>

      </main>

      {/* Verification Modal Overlays */}
      <AnimatePresence>
        {verificationResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-fixed cht-inset-0 cht-z-[9999] cht-flex cht-items-center cht-justify-center cht-bg-slate-950/70 cht-backdrop-blur-xl cht-p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="cht-bg-white/95 cht-backdrop-blur-2xl cht-w-full cht-max-w-lg cht-rounded-[36px] cht-p-8 sm:cht-p-10 cht-shadow-[0_32px_64px_rgba(0,0,0,0.15)] cht-border cht-border-white/50 cht-space-y-6 cht-text-center cht-relative"
            >
              {/* Top gradient highlight bar */}
              <div className="cht-absolute cht-top-0 cht-left-0 cht-right-0 cht-h-1.5 cht-bg-gradient-to-r cht-from-emerald-400 cht-via-teal-500 cht-to-indigo-500 cht-rounded-t-[36px]" />

              <button
                onClick={handleCloseVerification}
                className="cht-absolute cht-top-6 cht-right-6 cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-bg-slate-50 hover:cht-bg-slate-100 cht-rounded-full transition-colors"
              >
                <X size={16} className="cht-text-gray-500" />
              </button>

              {/* Verified Holographic Badge */}
              <div className="cht-w-20 cht-h-20 cht-bg-emerald-500/10 cht-text-emerald-600 cht-rounded-[28px] cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-inner cht-relative">
                <div className="cht-absolute cht-inset-0 cht-rounded-[28px] cht-bg-emerald-500/10 cht-animate-ping cht-opacity-20" />
                <ShieldCheck size={36} strokeWidth={1.8} />
              </div>

              <div className="cht-space-y-2">
                <h3 className="cht-text-xl sm:cht-text-2xl cht-font-black cht-text-black cht-tracking-tight">Credential Authenticated</h3>
                <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-bg-emerald-500/10 cht-text-[10px] cht-font-black cht-text-emerald-600 cht-uppercase cht-tracking-widest">
                  <CheckCircle2 size={12} /> {verificationResult.status}
                </span>
              </div>

              {/* Certificate Details */}
              <div className="cht-bg-slate-50/80 cht-p-6 cht-rounded-[24px] cht-border cht-border-slate-100/50 cht-space-y-4 cht-text-left">
                <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100/60 pb-3">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Recipient Name</span>
                  <span className="cht-text-sm cht-font-extrabold cht-text-slate-800">{verificationResult.student_name}</span>
                </div>
                <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100/60 pb-3">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Training Curricula</span>
                  <span className="cht-text-sm cht-font-extrabold cht-text-slate-800 cht-text-right max-w-[200px] cht-line-clamp-2">{verificationResult.curriculum_title}</span>
                </div>
                <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100/60 pb-3">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Credential Type</span>
                  <span className="cht-text-xs cht-font-extrabold cht-text-slate-800">{verificationResult.curriculum_type}</span>
                </div>
                <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100/60 pb-3">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Issue Date</span>
                  <span className="cht-text-xs cht-font-extrabold cht-text-slate-800">{verificationResult.issue_date}</span>
                </div>
                <div className="cht-flex cht-justify-between cht-items-baseline">
                  <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Registry ID</span>
                  <span className="cht-text-xs cht-font-mono cht-font-black cht-text-[#3e57da]">{verificationResult.certificate_id}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-3.5">
                <Button
                  variant="primary"
                  className="cht-flex-1 cht-h-12 cht-rounded-xl cht-bg-gradient-to-r cht-from-emerald-500 cht-to-teal-600 hover:cht-from-emerald-600 hover:cht-to-teal-700 cht-border-none cht-text-white shadow-lg shadow-emerald-500/20"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/verify-certificate?id=${verificationResult.certificate_id}`);
                    addToast({
                      message: "Cryptographic verification link copied!",
                      bg: "success"
                    });
                  }}
                  leftIcon={<Share2 size={16} />}
                >
                  Share Registry
                </Button>
                <Button
                  variant="outline"
                  className="cht-flex-1 cht-h-12 cht-rounded-xl"
                  onClick={handleCloseVerification}
                >
                  Close Registry
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {verificationError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-fixed cht-inset-0 cht-z-[9999] cht-flex cht-items-center cht-justify-center cht-bg-slate-950/70 cht-backdrop-blur-xl cht-p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="cht-bg-white/90 cht-backdrop-blur-2xl cht-w-full cht-max-w-md cht-rounded-[36px] cht-p-8 sm:cht-p-10 cht-shadow-[0_32px_64px_rgba(0,0,0,0.15)] cht-border cht-border-white/50 cht-space-y-6 cht-text-center cht-relative"
            >
              {/* Top gradient highlight bar */}
              <div className="cht-absolute cht-top-0 cht-left-0 cht-right-0 cht-h-1.5 cht-bg-gradient-to-r cht-from-amber-500 cht-via-orange-600 cht-to-rose-600 cht-rounded-t-[36px]" />

              <button
                onClick={handleCloseVerification}
                className="cht-absolute cht-top-6 cht-right-6 cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-bg-slate-50 hover:cht-bg-slate-100 cht-rounded-full transition-colors"
              >
                <X size={16} className="cht-text-gray-500" />
              </button>

              {/* Failed Badge */}
              <div className="cht-w-20 cht-h-20 cht-bg-rose-500/10 cht-text-rose-600 cht-rounded-[28px] cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-inner cht-relative">
                <div className="cht-absolute cht-inset-0 cht-rounded-[28px] cht-bg-rose-500/10 cht-animate-ping cht-opacity-20" />
                <AlertTriangle size={36} strokeWidth={1.8} />
              </div>

              <div className="cht-space-y-2">
                <h3 className="cht-text-xl sm:cht-text-2xl cht-font-black cht-text-black cht-tracking-tight">Verification Failed</h3>
                <p className="cht-text-xs cht-text-gray-400 font-bold uppercase tracking-wider">Invalid Certificate reference</p>
              </div>

              <div className="cht-bg-rose-50/50 cht-p-5 cht-rounded-[24px] cht-border cht-border-rose-100/50 cht-text-left">
                <p className="cht-text-[10px] cht-font-black cht-text-rose-600 cht-uppercase cht-tracking-widest">Database Report</p>
                <p className="cht-text-xs cht-text-slate-600 cht-font-bold cht-mt-1.5 leading-relaxed">
                  {verificationError}
                </p>
              </div>

              <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-3.5">
                <Button
                  variant="primary"
                  className="cht-flex-1 cht-h-12 cht-rounded-xl cht-bg-gradient-to-r cht-from-orange-500 cht-to-rose-500 hover:cht-from-orange-600 hover:cht-to-rose-600 cht-border-none cht-text-white shadow-lg shadow-orange-500/20"
                  onClick={handleCloseVerification}
                >
                  Try Different ID
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Certification;
